import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { OpenHouseVO } from 'domain/listing/value-objects';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import type { OpenHouse } from 'data/listing';
import { OpenHouseFormatter, type OpenHouseDisplayData } from './format-open-house';

export interface OpenHouseData {
  openHouses: OpenHouseDisplayData[];
  label: string;
  hasValue: boolean;
}

export function useOpenHouse(openHouses: OpenHouse[] | null): OpenHouseData {
  const { t } = useTranslation(Namespaces.COMMON);
  const { locale, formatDateWithTemplate, formatTime, timezone } = useI18nFormatting();

  return useMemo(() => {
    // Use OpenHouseVO to filter future open houses (business logic in VO)
    const futureOpenHouses = OpenHouseVO.filterFuture(openHouses);

    if (futureOpenHouses.length === 0) {
      return {
        openHouses: [],
        label: '',
        hasValue: false,
      };
    }

    const formatter = new OpenHouseFormatter({
      locale,
      timezone,
      formatDateWithTemplate,
      formatTime,
    });

    // Pass raw data to formatter for compatibility
    const formattedOpenHouses = formatter.formatAll(futureOpenHouses.map(vo => vo.raw));

    return {
      openHouses: formattedOpenHouses,
      label: t(LOCALE_COMMON_PATHS.OPEN_HOUSE_LABEL, { defaultValue: 'Open House' }),
      hasValue: true,
    };
  }, [openHouses, locale, timezone, formatDateWithTemplate, formatTime, t]);
}
